import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Trash2, X as XIcon, Pencil, Package as PackageIcon, Eye, EyeOff, Layers, Search, Star } from "lucide-react";
import { Card, Btn, Input, Field } from "@/components/admin/ui";
import { API, BACKEND_URL } from "@/config";
import { fmtErr } from "@/context/AuthProvider";
import { StrollerAttributesEditor } from "@/pages/admin/StrollerAttributesEditor";

const emptyBundle = {
  name: "",
  tagline: "",
  description: "",
  category: "",
  subcategory: "",
  image: "",
  tier_1_3: 30,
  tier_4_7: 25,
  tier_8_plus: 21,
  features: [],
  free_accessories: [],
  min_age: "",
  stroller_attributes: null,
  components: [],
  active: true,
  website_visible: true,
  featured: false,
};

const IconAction = ({ children, onClick, title, testId, active = false, danger = false, accent = false }) => (
  <button
    type="button"
    onClick={onClick}
    title={title}
    data-testid={testId}
    className={`inline-flex items-center justify-center w-8 h-8 rounded-lg border transition-colors ${
      danger
        ? "border-red-500/25 text-red-400 hover:border-red-500/60 hover:bg-red-500/10"
        : accent && active
          ? "border-[#FFD166]/70 text-[#FFD166] bg-[#FFD166]/15"
          : active
            ? "border-[#FFD166]/60 text-[#FFD166] bg-[#FFD166]/10"
            : "border-[#FFD166]/20 text-[#B9C8D5] hover:border-[#FFD166]/50 hover:text-[#FFD166]"
    }`}
  >
    {children}
  </button>
);

const BundleCard = ({ b, onEdit, onDelete, onToggleVisibility, onToggleFeatured }) => {
  const visible = b.website_visible !== false;
  const featured = !!b.featured;
  const imgSrc = b.image ? (b.image.startsWith("/api/") ? `${BACKEND_URL}${b.image}` : b.image) : null;
  return (
    <Card className={`p-3 flex flex-col ${featured ? "border-[#FFD166]/60" : ""}`} data-testid={`bundle-card-${b.id}`}>
      <div className="relative rounded-xl overflow-hidden bg-black/40 aspect-[4/3] mb-3">
        {imgSrc ? (
          <img src={imgSrc} alt={b.name} className="w-full h-full object-cover" loading="lazy" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-[#B9C8D5]/50">
            <Layers className="w-8 h-8" />
          </div>
        )}
        <span className="absolute top-2 left-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest bg-black/70 text-[#FFD166] border border-[#FFD166]/40 px-2 py-0.5 rounded-full">
          <Layers className="w-3 h-3" /> Bundle
        </span>
        {featured && (
          <span className="absolute top-2 right-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest bg-[#FFD166] text-[#1a0a03] px-2 py-0.5 rounded-full font-semibold">
            <Star className="w-3 h-3 fill-current" /> Featured
          </span>
        )}
        {!visible && (
          <span className="absolute bottom-2 right-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest bg-black/70 text-[#B9C8D5] border border-white/15 px-2 py-0.5 rounded-full">
            <EyeOff className="w-3 h-3" /> Hidden
          </span>
        )}
      </div>

      <div className="flex-1 min-w-0">
        <div className="font-serif text-lg leading-tight truncate" title={b.name}>{b.name}</div>
        <div className="text-xs text-[#B9C8D5] mt-0.5 truncate">{b.tagline || "—"}</div>
        <div className="mt-2 text-[11px] text-[#B9C8D5] leading-relaxed">
          Contains: {(b.components || []).map((c) => `× ${c.quantity} ${c.product_name || c.product_id}`).join(" · ") || "—"}
        </div>
        <div className="mt-2">
          <span className="text-[#FFD166] font-serif text-xl">${Number(b.tier_1_3).toFixed(2)}</span>
          <span className="text-[10px] text-[#B9C8D5] ml-1">/day (1–3 nights)</span>
        </div>
      </div>

      <div className="mt-3 pt-3 border-t border-[#FFD166]/15 flex items-center gap-1">
        <IconAction onClick={onEdit} title="Edit" testId={`bundle-edit-${b.id}`}>
          <Pencil className="w-3.5 h-3.5" />
        </IconAction>
        <IconAction
          onClick={onToggleVisibility}
          title={visible ? "Hide from website" : "Show on website"}
          testId={`bundle-toggle-visibility-${b.id}`}
          active={!visible}
        >
          {visible ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
        </IconAction>
        <IconAction
          onClick={onToggleFeatured}
          title={featured ? "Remove from featured" : "Mark as featured"}
          testId={`bundle-toggle-featured-${b.id}`}
          active={featured}
          accent
        >
          <Star className={`w-3.5 h-3.5 ${featured ? "fill-current" : ""}`} />
        </IconAction>
        <div className="flex-1" />
        <IconAction onClick={onDelete} title="Delete" testId={`bundle-delete-${b.id}`} danger>
          <Trash2 className="w-3.5 h-3.5" />
        </IconAction>
      </div>
    </Card>
  );
};

const BundleForm = ({ draft, allProducts, cats, onClose, onSaved }) => {
  const [f, setF] = useState({
    ...emptyBundle,
    ...draft,
    components: draft?.components || [],
    features: Array.isArray(draft?.features) ? draft.features.join("\n") : (draft?.features || ""),
    free_accessories: Array.isArray(draft?.free_accessories) ? draft.free_accessories.join(", ") : (draft?.free_accessories || ""),
  });
  const [saving, setSaving] = useState(false);
  const isNew = !draft?.id;
  const subcats = (cats.find((c) => c.id === f.category)?.subcategories) || [];

  const addComponent = () => {
    setF((cur) => ({ ...cur, components: [...(cur.components || []), { product_id: "", quantity: 1 }] }));
  };
  const removeComponent = (idx) => {
    setF((cur) => ({ ...cur, components: cur.components.filter((_, i) => i !== idx) }));
  };
  const updateComponent = (idx, patch) => {
    setF((cur) => ({
      ...cur,
      components: cur.components.map((c, i) => (i === idx ? { ...c, ...patch } : c)),
    }));
  };

  const save = async () => {
    if (!f.name.trim()) { toast.error("Give the bundle a name."); return; }
    const cleanComponents = (f.components || [])
      .filter((c) => c.product_id)
      .map((c) => ({ product_id: c.product_id, quantity: Math.max(1, Number(c.quantity) || 1) }));
    if (cleanComponents.length === 0) { toast.error("Add at least one component product to the bundle."); return; }
    setSaving(true);
    try {
      const payload = {
        ...f,
        tier_1_3: Number(f.tier_1_3),
        tier_4_7: Number(f.tier_4_7),
        tier_8_plus: Number(f.tier_8_plus),
        features: Array.isArray(f.features)
          ? f.features
          : String(f.features || "").split("\n").map((s) => s.trim()).filter(Boolean),
        free_accessories: (Array.isArray(f.free_accessories)
          ? f.free_accessories
          : String(f.free_accessories || "").split(",")
        ).map((s) => String(s).trim()).filter(Boolean),
        min_age: f.min_age || "",
        stroller_attributes: f.stroller_attributes || null,
        components: cleanComponents,
        website_visible: f.website_visible !== false,
        active: f.active !== false,
      };
      if (isNew) {
        await axios.post(`${API}/admin/bundles`, payload);
      } else {
        await axios.patch(`${API}/admin/bundles/${draft.id}`, payload);
      }
      toast.success("Bundle saved");
      onSaved();
    } catch (e) {
      toast.error(fmtErr(e.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-sm flex items-start justify-center p-4 sm:p-6 overflow-y-auto" data-testid="bundle-form-modal">
      <Card className="p-6 w-full max-w-3xl my-6 max-h-[calc(100vh-3rem)] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-serif text-2xl">{isNew ? "New Bundle" : `Edit · ${draft.name}`}</h2>
          <button onClick={onClose} className="text-[#B9C8D5] hover:text-[#FFD166]" data-testid="bundle-form-close">
            <XIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Bundle name"><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="e.g. Zoe Trio" data-testid="bf-name" /></Field>
          <Field label="Tagline"><Input value={f.tagline} onChange={(e) => setF({ ...f, tagline: e.target.value })} placeholder="One-line hook" data-testid="bf-tagline" /></Field>
          <Field label="Category">
            <select value={f.category} onChange={(e) => setF({ ...f, category: e.target.value, subcategory: "" })} className="w-full bg-black/30 border border-[#FFD166]/25 text-[#F6F1E4] px-3 py-2 rounded-lg" data-testid="bf-category">
              <option value="">Choose…</option>
              {cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </Field>
          <Field label="Subcategory">
            <select value={f.subcategory} onChange={(e) => setF({ ...f, subcategory: e.target.value })} className="w-full bg-black/30 border border-[#FFD166]/25 text-[#F6F1E4] px-3 py-2 rounded-lg" disabled={!subcats.length} data-testid="bf-subcategory">
              <option value="">—</option>
              {subcats.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </Field>

          <Field label="1–3 nights ($/day)"><Input type="number" step="0.01" value={f.tier_1_3} onChange={(e) => setF({ ...f, tier_1_3: e.target.value })} data-testid="bf-tier1" /></Field>
          <Field label="4–7 nights ($/day)"><Input type="number" step="0.01" value={f.tier_4_7} onChange={(e) => setF({ ...f, tier_4_7: e.target.value })} data-testid="bf-tier2" /></Field>
          <Field label="8+ nights ($/day)"><Input type="number" step="0.01" value={f.tier_8_plus} onChange={(e) => setF({ ...f, tier_8_plus: e.target.value })} data-testid="bf-tier3" /></Field>

          <Field label="Age range"><Input value={f.min_age} onChange={(e) => setF({ ...f, min_age: e.target.value })} placeholder="e.g. 6 mo – 50 lbs" data-testid="bf-age" /></Field>

          <Field label="Image URL">
            <Input value={f.image} onChange={(e) => setF({ ...f, image: e.target.value })} placeholder="https://…  or  /api/static/… " data-testid="bf-image" />
            <div className="mt-2">
              <label className="inline-flex items-center gap-2 text-xs text-[#FFD166] cursor-pointer hover:underline">
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  data-testid="bf-image-upload"
                  onChange={async (e) => {
                    const file = e.target.files?.[0]; if (!file) return;
                    const fd = new FormData(); fd.append("file", file);
                    try {
                      const { data } = await axios.post(`${API}/admin/upload-image`, fd, { headers: { "Content-Type": "multipart/form-data" } });
                      setF((cur) => ({ ...cur, image: data.url }));
                      toast.success("Image uploaded");
                    } catch (err) {
                      toast.error(fmtErr(err.response?.data?.detail) || "Upload failed");
                    }
                  }}
                />
                Upload photo
              </label>
            </div>
          </Field>

          <Field label="Show on website">
            <label className="flex items-center gap-2 mt-2 cursor-pointer" data-testid="bf-website-visible">
              <input
                type="checkbox"
                checked={f.website_visible !== false}
                onChange={(e) => setF({ ...f, website_visible: e.target.checked })}
                className="accent-[#FFD166]"
              />
              <span className="text-sm text-[#F6F1E4]">
                {f.website_visible !== false ? "Visible to customers" : "Hidden — admin only"}
              </span>
            </label>
          </Field>
        </div>

        <Field label="Description">
          <textarea
            value={f.description}
            onChange={(e) => setF({ ...f, description: e.target.value })}
            className="w-full bg-black/30 border border-[#FFD166]/25 text-[#F6F1E4] px-3 py-2 rounded-lg min-h-[100px]"
            data-testid="bf-description"
          />
        </Field>

        <div className="mt-4">
          <label className="text-xs uppercase tracking-[0.2em] text-[#B9C8D5]">Feature list (one per line, shown as bullets)</label>
          <textarea
            rows="4"
            value={f.features}
            onChange={(e) => setF({ ...f, features: e.target.value })}
            className="mt-1 w-full bg-black/30 border border-[#FFD166]/25 text-[#F6F1E4] px-3 py-2 rounded-lg"
            placeholder={"Fits 3 kids with the trio seat\nOne-hand fold\nExtra-large canopy"}
            data-testid="bf-features"
          />
        </div>

        <div className="mt-6 border-t border-[#FFD166]/15 pt-5">
          <div className="text-xs uppercase tracking-[0.2em] text-[#FFD166]">Free accessories (always included)</div>
          <p className="text-[11px] text-[#B9C8D5] mt-1">Comma-separated list shown to customers as "Included with your rental ✨" (e.g. "Rain cover, Parent console").</p>
          <Input
            className="mt-2"
            value={f.free_accessories}
            onChange={(e) => setF({ ...f, free_accessories: e.target.value })}
            placeholder="Rain cover, Parent console"
            data-testid="bf-free-accessories"
          />
        </div>

        <div className="mt-6 border-t border-[#FFD166]/15 pt-5" data-testid="bf-safety-section">
          <div className="text-xs uppercase tracking-[0.2em] text-[#FFD166]">Checkout safety acknowledgments</div>
          <p className="text-[11px] text-[#B9C8D5] mt-1">Component products' own flags also apply automatically; tick these to force one for the whole bundle.</p>
          <div className="mt-2 space-y-2">
            <label className="flex items-center gap-2 cursor-pointer text-sm text-[#F6F1E4]"><input type="checkbox" checked={!!f.requires_sleep_acknowledgment} onChange={(e) => setF({ ...f, requires_sleep_acknowledgment: e.target.checked })} className="accent-[#F4A93A]" data-testid="bf-requires-sleep" /> Sleep Equipment acknowledgment</label>
            <label className="flex items-center gap-2 cursor-pointer text-sm text-[#F6F1E4]"><input type="checkbox" checked={!!f.requires_mobility_acknowledgment} onChange={(e) => setF({ ...f, requires_mobility_acknowledgment: e.target.checked })} className="accent-[#F4A93A]" data-testid="bf-requires-mobility" /> Mobility Equipment acknowledgment</label>
          </div>
        </div>

        <div className="mt-6 border-t border-[#FFD166]/15 pt-5" data-testid="bf-guide-section">
          <div className="text-xs uppercase tracking-[0.2em] text-[#FFD166]">How-to guide intro (optional)</div>
          <p className="text-[11px] text-[#B9C8D5] mt-1">
            Shown above each component's own guide when a customer scans the tag QR{f.id ? <> (<a href={`/guide/${f.id}`} target="_blank" rel="noreferrer" className="text-[#FFD166] underline">/guide/{f.id}</a>)</> : null}. Markdown supported.
          </p>
          <textarea rows="4" value={f.guide_markdown || ""} onChange={(e) => setF({ ...f, guide_markdown: e.target.value })} className="mt-2 w-full bg-black/30 border border-[#FFD166]/25 text-[#F6F1E4] px-3 py-2 rounded-lg text-sm font-mono" placeholder="## Setting up your bundle…" data-testid="bf-guide-markdown" />
        </div>


        <div className="mt-4">
          <div className="flex items-center justify-between mb-2">
            <label className="text-xs uppercase tracking-[0.2em] text-[#B9C8D5]">Components</label>
            <button type="button" onClick={addComponent} className="text-[11px] text-[#FFD166] hover:underline" data-testid="bf-add-component">
              + Add component
            </button>
          </div>
          <div className="space-y-2">
            {(f.components || []).length === 0 && (
              <div className="text-xs text-[#B9C8D5] p-3 border border-dashed border-[#FFD166]/25 rounded-lg text-center">
                No components yet — click "Add component" to include a product.
              </div>
            )}
            {(f.components || []).map((c, i) => (
              <div key={i} className="grid grid-cols-[1fr_100px_40px] gap-2 items-center" data-testid={`bf-component-row-${i}`}>
                <select
                  value={c.product_id}
                  onChange={(e) => updateComponent(i, { product_id: e.target.value })}
                  className="w-full bg-black/30 border border-[#FFD166]/25 text-[#F6F1E4] px-3 py-2 rounded-lg text-sm"
                  data-testid={`bf-component-${i}-product`}
                >
                  <option value="">Pick a product…</option>
                  {allProducts.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
                <Input
                  type="number"
                  min="1"
                  value={c.quantity}
                  onChange={(e) => updateComponent(i, { quantity: e.target.value })}
                  data-testid={`bf-component-${i}-qty`}
                />
                <button
                  type="button"
                  onClick={() => removeComponent(i)}
                  className="text-red-400 hover:bg-red-500/10 rounded-lg p-2"
                  title="Remove component"
                  data-testid={`bf-component-${i}-remove`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
          <p className="text-[10px] text-[#B9C8D5]/70 mt-2">
            Booking one bundle deducts each listed product × quantity from its own inventory.
          </p>
        </div>

        <StrollerAttributesEditor
          value={f.stroller_attributes}
          onChange={(v) => setF({ ...f, stroller_attributes: v })}
        />

        <div className="mt-6 flex items-center gap-3 justify-end">
          <Btn variant="ghost" onClick={onClose} data-testid="bf-cancel">Cancel</Btn>
          <Btn onClick={save} disabled={saving} data-testid="bf-save">
            {saving ? "Saving…" : "Save bundle"}
          </Btn>
        </div>
      </Card>
    </div>
  );
};

export const Bundles = () => {
  const [items, setItems] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [cats, setCats] = useState([]);
  const [editing, setEditing] = useState(null);
  const [query, setQuery] = useState("");

  const load = useCallback(async () => {
    const [b, p, c] = await Promise.all([
      axios.get(`${API}/admin/bundles`),
      axios.get(`${API}/admin/products`),
      axios.get(`${API}/admin/categories`),
    ]);
    setItems(b.data.bundles || []);
    setAllProducts((p.data.products || []).filter((x) => x.active !== false));
    setCats(c.data.categories || []);
  }, []);
  useEffect(() => { load(); }, [load]);

  const del = async (id) => {
    if (!window.confirm("Delete this bundle? Existing reservations that reference it are unaffected.")) return;
    try {
      await axios.delete(`${API}/admin/bundles/${id}`);
      toast.success("Bundle deleted");
      load();
    } catch (e) {
      toast.error(fmtErr(e.response?.data?.detail) || "Delete failed");
    }
  };

  const toggleField = async (b, field, nextValue, successMsg) => {
    setItems((cur) => cur.map((x) => (x.id === b.id ? { ...x, [field]: nextValue } : x)));
    try {
      await axios.patch(`${API}/admin/bundles/${b.id}`, { [field]: nextValue });
      toast.success(successMsg);
      load();
    } catch (e) {
      setItems((cur) => cur.map((x) => (x.id === b.id ? { ...x, [field]: !nextValue } : x)));
      toast.error(fmtErr(e.response?.data?.detail) || "Update failed");
    }
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((b) => (b.name || "").toLowerCase().includes(q) || (b.tagline || "").toLowerCase().includes(q));
  }, [items, query]);

  return (
    <div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="font-serif text-4xl">Bundles</h1>
          <p className="text-sm text-[#B9C8D5] mt-1">{items.length} total · group multiple products into one purchasable listing</p>
        </div>
        <Btn onClick={() => setEditing({ ...emptyBundle })} data-testid="bundle-add-btn"><Plus className="w-3.5 h-3.5" /> New Bundle</Btn>
      </div>

      <div className="relative mb-6">
        <Search className="w-4 h-4 text-[#B9C8D5] absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search bundles by name or tagline…"
          className="pl-9"
          data-testid="bundles-search"
        />
      </div>

      {filtered.length === 0 ? (
        <Card className="p-10 text-center" data-testid="bundles-empty">
          <PackageIcon className="w-8 h-8 text-[#B9C8D5] mx-auto mb-3 opacity-60" />
          <div className="text-[#F6F1E4] font-medium">No bundles yet</div>
          <div className="text-xs text-[#B9C8D5] mt-1">Click "New Bundle" to group products together.</div>
        </Card>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" data-testid="bundles-grid">
          {filtered.map((b) => (
            <BundleCard
              key={b.id}
              b={b}
              onEdit={() => setEditing({ ...b })}
              onDelete={() => del(b.id)}
              onToggleVisibility={() => toggleField(b, "website_visible", !(b.website_visible !== false), b.website_visible !== false ? "Hidden from website" : "Visible on website")}
              onToggleFeatured={() => toggleField(b, "featured", !b.featured, !b.featured ? "Marked as featured" : "Removed from featured")}
            />
          ))}
        </div>
      )}

      {editing && (
        <BundleForm
          draft={editing}
          allProducts={allProducts}
          cats={cats}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
};
